import type { MessagesSearchResponse } from '@lio/api-types';
import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/material';
import { VirtualizedMessages, VirtualizedMessagesProps } from './components/VirtualizedMessages';
import { CardForm as SendNewMessageView } from '@pages/QuoteDetails/sidebarItems/notes/noteMessageForm/CardForm';
import { NoteType } from '@models/note';
import { useState, DragEvent, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { useStore } from '@store/GlobalStore';
import { Bell, Envelope, FileUpload } from '@assets/svgs';
import { EmptyMessagesList } from '@pages/QuoteDetails/sidebarItems/messages/EmptyMessagesList';
import { shallow } from 'zustand/shallow';
import { Dropzone } from '@components/drawerItem';
import FileDropWrapper from '@utils/FileDropWrapper';
import { isDataTransfer } from '@utils/fileUtils';
import Loading from '@components/Loading';

export interface ChatViewProps {
  isHidden: boolean; // hides while search mode active
  isLoadingInitialView?: boolean;
  messages: MessagesSearchResponse['items'];
  scrollRef: RefObject<HTMLDivElement | null>;
  loadOlder: () => void;
  loadNewer: () => void;
  hasOlder: boolean;
  hasNewer: boolean;
  isLoadingOlder: boolean;
  isLoadingNewer: boolean;
  anchorId?: string | null;
  initialScrollToBottom: boolean;
  onSendNewMessage: () => void;
  draggedFiles: File[];
  setDraggedFiles: Dispatch<SetStateAction<File[]>>;
  events?: {
    id: string;
    date: Date;
    text: string;
  }[];

  showEmptyList?: boolean;

  //
  policyId?: string;
  quoteId?: string;

  config?: {
    chatContainer: {
      backgroundColor?: string;
    };
    newMessage?: {
      banner?: string;
      disabled?: boolean;
    };
  };
}

export const ChatView = ({
  isHidden,
  isLoadingInitialView,
  messages,
  scrollRef,
  loadOlder,
  loadNewer,
  hasOlder,
  hasNewer,
  isLoadingOlder,
  isLoadingNewer,
  anchorId,
  initialScrollToBottom,
  onSendNewMessage,
  policyId,
  draggedFiles,
  setDraggedFiles,
  events,
  quoteId,
  showEmptyList,
  config,
}: ChatViewProps) => {
  const { isInternalUser } = useStore((state) => state.userAuth, shallow);

  const emptyMessageListItems = [
    {
      icon: <Bell />,
      text: `The assigned ${
        isInternalUser ? 'brokers' : 'underwriters'
      } are notified of your messages and receive email even if they aren't logged into the LIO Portal.`,
    },
    {
      icon: <Envelope />,
      text: `The messages will also be sent via email to the assigned ${
        isInternalUser ? 'brokers' : 'underwriter'
      }. Any documents that you choose to include will be attached.`,
    },
    {
      icon: <FileUpload />,
      text: 'You can share images, videos, and other files by dragging and dropping them here or by browsing your files manually.',
    },
  ];

  const earliestLoadedMessage = messages?.[0];
  const latestLoadedMessage = messages?.[messages.length - 1];

  // find any events that are within (earliestLoadedMessage and latestLoadedMessage)
  // if hasNewer is false, add any events after latestLoadedMessage
  // if hasOlder = false and there are events before earliestLoadedMessage, include those too
  const eventsInRange =
    events?.filter((event) => {
      if (!earliestLoadedMessage || !latestLoadedMessage) return false;
      return event.date >= earliestLoadedMessage.createdAt && event.date <= latestLoadedMessage.createdAt;
    }) ?? [];

  if (!hasNewer) {
    const additionalEvents = events?.filter((event) => {
      if (!latestLoadedMessage) return false;
      return event.date > latestLoadedMessage.createdAt;
    });
    eventsInRange.push(...(additionalEvents || []));
  }

  if (!hasOlder) {
    const olderEvents = events?.filter((event) => {
      if (!earliestLoadedMessage) return false;
      return event.date < earliestLoadedMessage.createdAt;
    });
    eventsInRange.push(...(olderEvents || []));
  }

  // in order of event.date compared to message.createdAt insert events in order
  const messagesWithEvents: VirtualizedMessagesProps['messages'] = [...messages];

  eventsInRange.forEach((event) => {
    // Map event to a message-like object with required fields
    const eventAsMessage: VirtualizedMessagesProps['messages'][number] = {
      id: event.id,
      createdAt: event.date,
      updatedAt: event.date,
      createdByUser: undefined,
      body: event.text,
      artifacts: [],
      policyId: policyId,
      type: 'SYSTEM',
    };
    const index = messagesWithEvents.findIndex((message) => message.createdAt && message.createdAt > event.date);
    if (index === -1) {
      messagesWithEvents.push(eventAsMessage);
    } else {
      messagesWithEvents.splice(index, 0, eventAsMessage);
    }
  });

  const fullWidthMode = !!quoteId;

  const [dragging, setDragging] = useState(false);
  const [isEditorDialogOpen, setIsEditorDialogOpen] = useState(false);

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isDataTransfer(e)) {
      setDragging(true);
    }
  };
  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const newDraggedFiles: File[] = [];
    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i += 1) {
        if (e.dataTransfer.items[i].kind === 'file') {
          const file = e.dataTransfer.items[i].getAsFile();
          if (file !== null) {
            newDraggedFiles.push(file);
          }
        }
      }
      setDraggedFiles(newDraggedFiles);
    }
  };

  return (
    <FileDropWrapper
      handleDragOver={handleDragOver}
      handleDragLeave={handleDragLeave}
      handleDrop={handleDrop}
      disabled={isEditorDialogOpen}
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}
    >
      {dragging ? (
        <Dropzone />
      ) : (
        <>
          <Grid
            id='message-container'
            size={{ xs: 12 }}
            sx={{
              backgroundColor: config?.chatContainer?.backgroundColor ?? 'background.main',
              flex: 1,
              position: 'relative',
              overflowY: 'auto',
              px: fullWidthMode ? 0 : 2,
              height: isHidden ? '0px' : 'auto',
              maxHeight: isHidden ? '0px' : 'auto',
              visibility: isHidden ? 'hidden' : 'visible',
            }}
            ref={scrollRef}
          >
            {isLoadingInitialView && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                <Loading />
              </Box>
            )}
            {showEmptyList ? (
              <Grid
                container
                sx={fullWidthMode ? {} : { justifyContent: 'center', height: '100%', alignItems: 'center' }}
              >
                <EmptyMessagesList
                  title={`Chat with ${isInternalUser ? 'brokers' : 'your underwriter'}`}
                  subtitle={`If you have any questions or comments about this quote you can message ${
                    isInternalUser ? 'brokers' : 'your underwriter'
                  } directly from right here.`}
                  items={emptyMessageListItems}
                />
              </Grid>
            ) : (
              <Box
                sx={{
                  visibility: !initialScrollToBottom ? 'hidden' : 'visible',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: fullWidthMode ? 1 : 2,
                  pb: 4,
                }}
              >
                <VirtualizedMessages
                  fullWidthMode={fullWidthMode}
                  messages={messagesWithEvents}
                  scrollRef={scrollRef}
                  loadOlder={loadOlder}
                  loadNewer={loadNewer}
                  hasOlder={hasOlder}
                  hasNewer={hasNewer}
                  isLoadingOlder={isLoadingOlder}
                  isLoadingNewer={isLoadingNewer}
                  jumpToMessageId={anchorId ?? undefined}
                />
              </Box>
            )}
          </Grid>
          {config?.newMessage?.banner && (
            <Grid sx={{ backgroundColor: 'warning.dark', display: 'flex' }}>
              <Typography variant='bodySmall' fontSize='12px' sx={{ margin: '0 auto' }}>
                {config?.newMessage?.banner}
              </Typography>
            </Grid>
          )}
          <Grid
            size={{ xs: 12 }}
            sx={{
              height: isHidden ? '0' : 'auto',
              visibility: isHidden ? 'hidden' : 'visible',
              borderTop: 1,
              borderColor: 'divider',
              pt: 1,
              px: 2,
            }}
          >
            <SendNewMessageView // <CardForm (to find if searching by component name)
              type={NoteType.Message}
              draggedFiles={draggedFiles}
              resetDraggedFiles={() => setDraggedFiles([])}
              onDialogOpenChange={setIsEditorDialogOpen}
              isDisabled={config?.newMessage?.disabled}
              sxProps={{
                editorContainer: {
                  padding: '0px 1px 8px 1px',
                },
              }}
              hideIncludedDocumentsSection={!!policyId}
              opts={{
                onSendNewMessage: onSendNewMessage,
                policyId,
                sendOnEnter: false,
                autofocus: true,
              }}
            />
          </Grid>
        </>
      )}
    </FileDropWrapper>
  );
};
